import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import LockReset from '@mui/icons-material/LockReset';
import { Parent, Student, CrudPermissions } from '../../types';
import { useData } from '../../contexts/DataContext';

interface ParentsManagementProps {
  permissions: CrudPermissions;
}
import { v4 as uuidv4 } from 'uuid';

export const ParentsManagement: React.FC<ParentsManagementProps> = ({ permissions }) => {
  const { parents, setParents, students } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingParent, setEditingParent] = useState<Parent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<Partial<Parent>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    studentIds: [],
  });

  const filteredParents = useMemo(() => {
    return parents.filter((parent) => {
      const matchesSearch =
        parent.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        parent.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        parent.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        parent.phone.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [parents, searchQuery]);

  const getStudentNames = (studentIds: string[]): string => {
    const matchedStudents = students.filter(s => studentIds.includes(s.id));
    if (matchedStudents.length === 0) return 'No students assigned';
    return matchedStudents.map(s => `${s.firstName} ${s.lastName} (${s.studentIdNumber})`).join(', ');
  };

  const handleAdd = () => {
    setEditingParent(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      studentIds: [],
    });
    setOpenDialog(true);
  };

  const handleEdit = (parent: Parent) => {
    setEditingParent(parent);
    setFormData(parent);
    setOpenDialog(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this parent?')) {
      setParents(parents.filter((p) => p.id !== id));
      const index = mockParents.findIndex((p) => p.id === id);
      if (index > -1) {
        mockParents.splice(index, 1);
      }
    }
  };

  const handlePasswordReset = () => {
    alert('Password reset email sent to ' + formData.email);
  };

  const handleSave = () => {
    const parentId = editingParent?.id || uuidv4();
    const oldStudentIds = editingParent?.studentIds || [];
    const newStudentIds = formData.studentIds || [];

    if (editingParent) {
      const updated = parents.map((p) =>
        p.id === editingParent.id ? { ...p, ...formData } : p
      );
      setParents(updated);
      const index = mockParents.findIndex((p) => p.id === editingParent.id);
      if (index > -1) {
        mockParents[index] = { ...mockParents[index], ...formData } as Parent;
      }
    } else {
      const newParent: Parent = {
        id: parentId,
        firstName: formData.firstName!,
        lastName: formData.lastName!,
        email: formData.email!,
        phone: formData.phone!,
        studentIds: newStudentIds,
      };
      setParents([...parents, newParent]);
      mockParents.push(newParent);
    }

    const studentsToRemove = oldStudentIds.filter(id => !newStudentIds.includes(id));
    const studentsToAdd = newStudentIds.filter(id => !oldStudentIds.includes(id));

    studentsToRemove.forEach(studentId => {
      const student = students.find(s => s.id === studentId);
      if (student) {
        student.parentIds = student.parentIds.filter(id => id !== parentId);
      }
    });

    studentsToAdd.forEach(studentId => {
      const student = students.find(s => s.id === studentId);
      if (student && !student.parentIds.includes(parentId)) {
        student.parentIds.push(parentId);
      }
    });

    setOpenDialog(false);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="h5" fontWeight={600}>
          Parents Management
        </Typography>
        {permissions.create && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAdd}
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
              flexShrink: 0,
            }}
          >
            Add Parent
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          placeholder="Search parents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {filteredParents.map((parent) => (
          <Box
            key={parent.id}
            sx={{
              display: 'flex',
              minWidth: 0,
            }}
          >
            <Card sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
            }}>
              <CardContent sx={{
                flexGrow: 1,
                minWidth: 0,
                maxWidth: '100%',
              }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mb: 2,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {parent.firstName} {parent.lastName}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {parent.email}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {parent.phone}
                </Typography>

                <Box>
                  <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                    Students:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {getStudentNames(parent.studentIds)}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                {permissions.update && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => handleEdit(parent)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                  >
                    Edit
                  </Button>
                )}
                {permissions.delete && (
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(parent.id)}
                    color="error"
                    sx={{ flexShrink: 0 }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Box>
        ))}
      </Box>

      {filteredParents.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No parents found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? 'Try adjusting your search'
              : 'Click "Add Parent" to create your first parent'}
          </Typography>
        </Box>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingParent ? 'Edit Parent' : 'Add New Parent'}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingParent}
                fullWidth
                helperText={editingParent ? "Email cannot be changed after creation" : ""}
              />
              {editingParent && (
                <IconButton
                  onClick={handlePasswordReset}
                  color="primary"
                  sx={{ mt: 1 }}
                  title="Send password reset email"
                >
                  <LockReset />
                </IconButton>
              )}
            </Box>
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <Autocomplete
              multiple
              options={students}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.studentIdNumber})`}
              value={students.filter(s => formData.studentIds?.includes(s.id))}
              onChange={(_, newValue) => {
                setFormData({ ...formData, studentIds: newValue.map(s => s.id) });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Students"
                  placeholder="Select students"
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">
                      {option.firstName} {option.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.studentIdNumber} | Grade {option.grade}
                    </Typography>
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={`${option.firstName} ${option.lastName} (${option.studentIdNumber})`}
                      {...tagProps}
                      size="small"
                    />
                  );
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
